"use client";

import { useRef, useState, type ChangeEvent } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CheckCircle2, FileUp, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { UphackFunctionSection } from "./UphackFunctionInput";
import {
  UPHACK_INPUT_MAX_BYTES,
  formatBytes,
  uphackInputBytes,
  uphackRefusalOf,
  useSendUphack,
} from "../../uphack";

/**
 * "Try to break it" (`EX-HCK01`, the canvas board `UphackSend`): one input
 * against one accepted program of a finished round. The size is measured as
 * the server measures it, live, and the send stays off while it is empty or
 * over 1 MB. What happens next is said before the press: the validator, the
 * setter's solution, the program, the setter. After the press the sheet says
 * it was sent and opens "Your hacks", where the judge's answer arrives.
 *
 * `EX-FNC06`: on a function problem (`target.answer`) the input is typed by
 * parameter (`UphackFunctionInput`) against the problem's signature, read
 * from the problem's own view, and the send waits until every call reads.
 */
export function UphackSheet({
  open,
  slug,
  target,
  onClose,
  onSent,
}: {
  open: boolean;
  slug: string;
  target: { submissionId: string; letter: string; name: string | null; answer?: "function" };
  onClose: () => void;
  /** Opens "Your hacks" after a send. */
  onSent: () => void;
}) {
  const t = useTranslations("contest.uphack");
  const locale = useLocale();
  const [input, setInput] = useState("");
  const [left, setLeft] = useState<number | null>(null);
  const file = useRef<HTMLInputElement | null>(null);
  const send = useSendUphack(slug);
  // `EX-FNC06`: a function problem's hack is typed by parameter, and the send
  // waits until every call reads against the signature.
  const functionProblem = target.answer === "function";
  const [fnReady, setFnReady] = useState(false);
  const [fileStamp, setFileStamp] = useState(0);

  const bytes = uphackInputBytes(input);
  const tooLarge = bytes > UPHACK_INPUT_MAX_BYTES;
  const empty = input.trim().length === 0;
  const shortId = target.submissionId.slice(-6);
  const refusal = send.isError ? uphackRefusalOf(send.error) : null;
  const sent = send.isSuccess;

  /** A picked file far over the cap, never read into the page: its size, for the line under the box. */
  const [oversizeFile, setOversizeFile] = useState<number | null>(null);

  const pickFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    send.reset();
    if (picked.size > UPHACK_INPUT_MAX_BYTES * 2) {
      setInput("");
      setOversizeFile(picked.size);
      return;
    }
    setOversizeFile(null);
    const text = await picked.text();
    setInput(text);
    // A picked file opens the function fields again on what it holds.
    setFileStamp((stamp) => stamp + 1);
  };

  const submit = () => {
    send.mutate(
      { submissionId: target.submissionId, input },
      { onSuccess: (data) => setLeft(data.left) },
    );
  };

  const steps = [t("stepValidator"), t("stepMain"), t("stepProgram", { name: target.name ?? t("theirProgram") }), t("stepSetter")];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("sheetTitle")}
      closeLabel={t("close")}
      dismissible={!send.isPending}
      leading={
        <span className="inline-flex h-7 items-center rounded-full bg-primary/10 px-2.5 font-mono text-xs font-bold text-primary-ink">
          {`${target.letter} · #${shortId}`}
        </span>
      }
    >
      {sent ? (
        <div className="space-y-4" role="status">
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            <div className="space-y-1">
              <p className="font-semibold text-emerald-900">{t("sentTitle")}</p>
              <p className="text-sm text-emerald-900/80">{t("sentBody")}</p>
            </div>
          </div>
          {left !== null ? <p className="text-xs text-neutral-500">{t("leftToday", { left })}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("close")}
            </Button>
            <Button type="button" onClick={onSent}>
              {t("seeYourHacks")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-neutral-700">{t("sheetBody")}</p>
          {functionProblem ? (
            <UphackFunctionSection
              slug={slug}
              letter={target.letter}
              value={input}
              fileStamp={fileStamp}
              onChange={(next) => {
                setOversizeFile(null);
                setInput(next.input);
                setFnReady(next.ready);
              }}
            />
          ) : (
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-neutral-800">{t("yourInput")}</span>
              <Textarea
                value={input}
                onChange={(event) => {
                  setOversizeFile(null);
                  setInput(event.target.value);
                }}
                rows={7}
                spellCheck={false}
                placeholder={t("inputPlaceholder")}
                className="font-mono text-sm"
                aria-describedby="uphack-size"
              />
            </label>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span
              id="uphack-size"
              className={cn("tabular-nums", tooLarge || oversizeFile !== null ? "font-semibold text-red-700" : "text-neutral-500")}
            >
              {oversizeFile !== null
                ? t("fileTooLarge", { size: formatBytes(oversizeFile, locale) })
                : tooLarge
                  ? t("tooLarge", { size: formatBytes(bytes, locale) })
                  : t("size", { size: formatBytes(bytes, locale) })}
            </span>
            <input ref={file} type="file" className="sr-only" tabIndex={-1} aria-hidden onChange={pickFile} />
            <Button type="button" variant="tertiary" size="sm" onClick={() => file.current?.click()}>
              <FileUp className="h-3.5 w-3.5" aria-hidden />
              {t("pickFile")}
            </Button>
          </div>

          <ol className="space-y-2 rounded-xl border border-neutral-100 bg-neutral-50 p-3.5">
            <li className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500">{t("whatHappens")}</li>
            {steps.map((step, index) => (
              <li key={step} className="flex items-center gap-2.5 text-sm text-neutral-700">
                <span
                  aria-hidden
                  className={cn(
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors",
                    send.isPending && index === 0 ? "animate-pulse bg-primary/15 text-primary-ink" : "bg-white text-neutral-500 ring-1 ring-neutral-200",
                  )}
                >
                  {new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-US").format(index + 1)}
                </span>
                {step}
              </li>
            ))}
          </ol>

          {refusal ? (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
              {t(`refusal_${refusal}`)}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-sm text-xs leading-relaxed text-neutral-500">{t("roundSafe")}</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={send.isPending}>
                {t("cancel")}
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={empty || tooLarge || send.isPending || (functionProblem && !fnReady)}
              >
                <Wrench className="h-4 w-4" aria-hidden />
                {send.isPending ? t("sending") : t("send")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
